import React, { useEffect, useState } from 'react';
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from 'react-router-dom';
import { TabNavigation } from './components/TabNavigation';
import { CompanyProfile } from './views/CompanyProfile';
import { CompanyConfiguration } from './views/CompanyConfiguration/CompanyConfiguration';
import { LogisticCosts } from './views/LogisticCosts/LogisticCosts';
import { DiscountsAndProfit } from './views/DiscountsAndProfit';
import { AdditionalCosts } from './views/AdditionalCosts/AdditionalCosts';
import { FinancingPlans } from './views/FinancingPlans';
import Accessories from './views/Accessories/Accessories.js';
import ProposalTemplateManager from './views/ProposalTemplateManager.jsx';
import { useGlobalState } from '../../stateProviders/GlobalStateProvider';
import { Box, Typography } from '@mui/material';
import {
	UnsavedChangesProvider,
	useUnsavedChangesContext,
} from '../../contexts/UnsavedChangesContext';
import { UnsavedFormChangesModal } from '../../globalComponents/modal/modalContent/UnsavedFormChangesModal';

const CompanyContent = () => {
	const globalState = useGlobalState();
	const location = useLocation();
	const navigate = useNavigate();
	const { hasUnsavedChanges, setHasUnsavedChanges, onSaveCallback } =
		useUnsavedChangesContext();
	const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
	const [pendingNavigation, setPendingNavigation] = useState<string | null>(
		null
	);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			if (!hasUnsavedChanges) return;

			const target = e.target as HTMLElement;
			const link = target.closest('a[href]');

			if (link) {
				const href = link.getAttribute('href');
				if (href && !location.pathname.includes(href)) {
					e.preventDefault();
					e.stopPropagation();
					setPendingNavigation(href);
					setUnsavedModalOpen(true);
				}
			}
		};

		document.addEventListener('click', handleClick, true);
		return () => document.removeEventListener('click', handleClick, true);
	}, [hasUnsavedChanges, location.pathname]);

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [hasUnsavedChanges]);

	const handleSave = async () => {
		if (onSaveCallback) {
			setIsSaving(true);
			try {
				await onSaveCallback();
				setHasUnsavedChanges(false);
				setUnsavedModalOpen(false);
				setPendingNavigation(null);
			} finally {
				setIsSaving(false);
			}
		}
	};

	const handleSaveAndContinue = async () => {
		if (onSaveCallback) {
			setIsSaving(true);
			try {
				await onSaveCallback();
				setHasUnsavedChanges(false);
				setUnsavedModalOpen(false);
				if (pendingNavigation) {
					navigate(pendingNavigation);
					setPendingNavigation(null);
				}
			} finally {
				setIsSaving(false);
			}
		}
	};

	const handleContinueWithoutSaving = () => {
		setHasUnsavedChanges(false);
		setUnsavedModalOpen(false);
		if (pendingNavigation) {
			navigate(pendingNavigation);
			setPendingNavigation(null);
		}
	};

	const handleCancel = () => {
		setUnsavedModalOpen(false);
		setPendingNavigation(null);
	};

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					overflow: 'hidden',
					rowGap: 3,
				}}
				data-testid="company-page"
			>
				<Typography variant="h1" fontWeight={600}>
					My Company
				</Typography>
				<TabNavigation />
				<Box
					sx={{
						flexGrow: 1,
						overflow: 'auto',
						scrollbarWidth: 'none',
						scrollbarColor: (theme) =>
							`${theme.palette.gray['400']} transparent`,
					}}
				>
					<Routes>
						<Route path="profile" element={<CompanyProfile />} />
						<Route path="proposal" element={<ProposalTemplateManager />} />
						<Route path="logistics" element={<LogisticCosts />} />
						<Route path="additional-costs" element={<AdditionalCosts />} />
						<Route path="accessories" element={<Accessories />} />
						<Route path="discounts-profit" element={<DiscountsAndProfit />} />
						<Route path="financing" element={<FinancingPlans />} />
						<Route path="config" element={<CompanyConfiguration />} />
						{globalState.isSuperAdmin && (
							<Route path="config" element={<CompanyConfiguration />} />
						)}
						<Route path="*" element={<Navigate to="profile" />} />
					</Routes>
				</Box>
			</Box>

			<UnsavedFormChangesModal
				open={unsavedModalOpen}
				onSave={handleSave}
				onSaveAndContinue={handleSaveAndContinue}
				onContinueWithoutSaving={handleContinueWithoutSaving}
				onCancel={handleCancel}
				isSaving={isSaving}
			/>
		</>
	);
};

export const Company = () => {
	return (
		<UnsavedChangesProvider>
			<CompanyContent />
		</UnsavedChangesProvider>
	);
};
